import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Stack,
  Chip,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import { ReferralPacket, ReferralDocument } from '~/types/coordinate';
import DocumentPreviewDialog from './DocumentPreviewDialog';

const TYPE_LABELS: Record<ReferralDocument['type'], string> = {
  discharge_summary: 'Discharge Summary',
  pt_ot_notes: 'PT/OT Notes',
  wound_notes: 'Wound Care Notes',
  physician_orders: 'Physician Orders',
  insurance_auth: 'Insurance Authorization',
  agency_narrative: 'Agency Narrative',
};

interface Props {
  packet: ReferralPacket;
}

const ReferralPacketViewer: React.FC<Props> = ({ packet }) => {
  const [tab, setTab] = useState(0);
  const [previewDoc, setPreviewDoc] = useState<ReferralDocument | null>(null);

  return (
    <Box>
      <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700}>Referral Packet</Typography>
        <Typography variant="caption" color="text.secondary">
          Assembled {packet.assembledAt}
        </Typography>
      </Stack>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="Patient" />
        <Tab label="Insurance" />
        <Tab label="Clinical Summary" />
        <Tab label="Orders" />
        <Tab label={`Documents (${packet.documents.length})`} />
      </Tabs>

      {tab === 0 && (
        <Stack spacing={1}>
          <Row label="Name" value={packet.patient.name} />
          <Row label="Date of Birth" value={packet.patient.dob} />
          <Row label="Address" value={packet.patient.address} />
          <Row label="Phone" value={packet.patient.phone} />
        </Stack>
      )}

      {tab === 1 && (
        <Stack spacing={1}>
          <Row label="Insurance Plan" value={packet.insurance.plan} />
          <Row label="Member ID" value={packet.insurance.memberId} />
        </Stack>
      )}

      {tab === 2 && (
        <Stack spacing={2}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>DIAGNOSES</Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.5 }}>
              {packet.clinicalSummary.diagnoses.map(d => (
                <Chip key={d} label={d} size="small" />
              ))}
            </Stack>
          </Box>
          <Divider />
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>KEY CONDITIONS</Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.5 }}>
              {packet.clinicalSummary.keyConditions.map(c => (
                <Chip key={c} label={c} size="small" variant="outlined" />
              ))}
            </Stack>
          </Box>
          <Divider />
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>RECENT NOTES</Typography>
            <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.7 }}>
              {packet.clinicalSummary.recentNotes}
            </Typography>
          </Box>
        </Stack>
      )}

      {tab === 3 && (
        <Stack spacing={0.5}>
          {packet.orders.map((order, i) => (
            <Typography key={i} variant="body2">• {order}</Typography>
          ))}
        </Stack>
      )}

      {tab === 4 && (
        <List disablePadding>
          {packet.documents.map(doc => (
            <React.Fragment key={doc.id}>
              <ListItem disablePadding sx={{ py: 1 }}>
                <DescriptionIcon sx={{ mr: 1.5, color: 'text.secondary', fontSize: '1.2rem' }} />
                <ListItemText
                  primary={doc.title}
                  secondary={`${TYPE_LABELS[doc.type]} · ${doc.dateCreated}`}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
                <ListItemSecondaryAction>
                  <Button size="small" variant="outlined" onClick={() => setPreviewDoc(doc)}>
                    Preview
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
              <Divider />
            </React.Fragment>
          ))}
        </List>
      )}

      <DocumentPreviewDialog document={previewDoc} onClose={() => setPreviewDoc(null)} />
    </Box>
  );
};

const Row = ({ label, value }: { label: string; value: string }) => (
  <Stack direction="row" spacing={2} alignItems="baseline">
    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ minWidth: 120 }}>
      {label.toUpperCase()}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Stack>
);

export default ReferralPacketViewer;
